import React, { useRef, useState, useEffect, useMemo } from "react";
import { useFamilyData } from "../hooks/useFamilyData";
import PinPad from "../components/PinPad";
import { formatTimeLabel, getTaskDueDate, isTaskDueSoon, isTaskOverdue, isWithinPowerHour } from "../utils/time";
import { createActionId, executeAction } from "../utils/actions";
import { runActionWithQueue } from "../utils/actionQueue";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "./ui/ToastProvider";
import { useModalA11y } from "../hooks/useModalA11y";
import { getEffectiveTaskStatus, isRepeatTask, shouldRepeatResetToday } from "../utils/taskStatus";

const getZonedDate = (date, timeZone) => {
  if (!timeZone || typeof Intl === "undefined") return new Date(date);
  try {
    return new Date(date.toLocaleString("en-US", { timeZone }));
  } catch (error) {
    return new Date(date);
  }
};

const getTimeZoneOffsetMs = (date, timeZone) =>
  getZonedDate(date, timeZone).getTime() - date.getTime();

const getLocalMidnightUtcMs = (date, timeZone) => {
  const zoned = getZonedDate(date, timeZone);
  const offsetMs = getTimeZoneOffsetMs(date, timeZone);
  const midnight = new Date(zoned);
  midnight.setHours(0, 0, 0, 0);
  return midnight.getTime() - offsetMs;
};

export default function KidBoard({
  familyId,
  familySettings,
  isKidAccount = false,
  currentUserId = null,
  activeMemberDisplayName = ""
}) {
  const { showToast } = useToast();
  const {
    tasks = [],
    kids = [],
    calendarEvents = [],
    rewards = [],
    redemptions = []
  } = useFamilyData(familyId);
  const isPro = familySettings?.isPro || false;
  const clarificationsEnabled = familySettings?.clarificationsEnabled !== false;
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [now, setNow] = useState(() => new Date());
  const [showPinPad, setShowPinPad] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isBoardLocked, setIsBoardLocked] = useState(familySettings?.toddlerLock || false);
  const inactivityTimerRef = useRef(null);
  const [taskView, setTaskView] = useState("today");
  const [rewardNoticeByKid, setRewardNoticeByKid] = useState({});
  const [clarifyDrafts, setClarifyDrafts] = useState({});
  const [replyModal, setReplyModal] = useState(null);
  const replyInputRef = useRef(null);
  const replyModalRef = useRef(null);
  const [keyboardShift, setKeyboardShift] = useState(false);
  const [useDeviceKeyboard, setUseDeviceKeyboard] = useState(false);
  const timeZone =
    familySettings?.timezone ||
    (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC");
  const zonedNow = useMemo(() => getZonedDate(now, timeZone), [now, timeZone]);
  const localMidnightUtcMs = useMemo(() => getLocalMidnightUtcMs(now, timeZone), [now, timeZone]);
  const graceMins = Number(familySettings?.autoFailGraceMins || 0);
  const autoFailActive = isPro && familySettings?.autoFailEnabled !== false;
  const normalizedMemberName = String(activeMemberDisplayName || "").trim().toLowerCase();
  const quickReplyOptions = [
    "Can you show me how to do this?",
    "I finished this. Can you check it?",
    "I need help with one step."
  ];

  useModalA11y({
    isOpen: Boolean(replyModal),
    onClose: () => setReplyModal(null),
    containerRef: replyModalRef,
    initialFocusRef: replyInputRef
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `kidTaskView:${familyId || "default"}`;
    const stored = window.localStorage.getItem(key);
    if (stored === "all" || stored === "today") {
      setTaskView(stored);
    }
  }, [familyId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `kidTaskView:${familyId || "default"}`;
    window.localStorage.setItem(key, taskView);
  }, [familyId, taskView]);

  // Auto-lock after 10 seconds of inactivity when toddler lock is enabled and unlocked.
  useEffect(() => {
    if (!familySettings?.toddlerLock || isBoardLocked) return;
    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => setIsBoardLocked(true), 10000);
    };

    resetTimer();
    const handleInteraction = () => resetTimer();
    window.addEventListener("pointerdown", handleInteraction);
    window.addEventListener("keydown", handleInteraction);
    window.addEventListener("touchstart", handleInteraction);

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      window.removeEventListener("pointerdown", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
    };
  }, [familySettings?.toddlerLock, isBoardLocked]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!replyModal) return;
    setKeyboardShift(false);
    setUseDeviceKeyboard(false);
    const timeout = setTimeout(() => replyInputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, [replyModal]);

  // --- INTERACTION HANDLER ---
  const handleInteraction = (task, type = "pending") => {
    if (task.assignedTo === "unassigned") {
      const kid = kids.find(k => k.id === type);
      if (kid?.isFrozen) return; 
    }

    if (isBoardLocked) {
      setPendingAction({ task, type });
      setShowPinPad(true);
    } else {
      executeTaskUpdate(task, type);
    }
  };

  const executeTaskUpdate = async (task, type) => {
    if (type === "pending") {
      // Find the kid's name to attach to the approval request
      const kid = kids.find(k => k.id === task.assignedTo);
      const actionId = createActionId();
      await runActionWithQueue(
        {
          type: "task.submit",
          familyId,
          taskId: task.id,
          actionId,
          kidName: kid?.name || "Someone"
        },
        executeAction
      );
    } else {
      const kid = kids.find(k => k.id === type);
      const actionId = createActionId();
      await runActionWithQueue(
        {
          type: "task.claim",
          familyId,
          taskId: task.id,
          actionId,
          kidId: type,
          kidName: kid?.name || ""
        },
        executeAction
      );
    }
  };

  // --- CALENDAR DATE COMPARISON (Fixed for String Dates) ---
  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;

    let date1;
    // 1. Handle Firestore Timestamp
    if (d1.toDate) {
      date1 = d1.toDate();
    } 
    // 2. Handle Date String "YYYY-MM-DD" from CalendarCard
    else if (typeof d1 === 'string') {
      const [year, month, day] = d1.split('-').map(Number);
      date1 = new Date(year, month - 1, day);
    } 
    // 3. Fallback
    else {
      date1 = new Date(d1);
    }

    const date2 = d2 instanceof Date ? d2 : new Date(d2);
    
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const getTaskTimeLabel = (task) => {
    const timeStr = formatTimeLabel(task.expiryTime || task.expirationTime || task.dueAt);
    if (!timeStr) return null;
    return `⏰ ${timeStr}`;
  };

  const isTaskDueToday = (task) => {
    const due = getTaskDueDate(task, zonedNow);
    if (!due) return true;
    return isSameDay(due, zonedNow);
  };

  const matchesView = (task) => {
    const status = getEffectiveStatus(task);
    if (taskView === "all") return true;
    if (status === "failed" || status === "pending") return true;
    return isTaskDueToday(task);
  };

  const getClarifications = (task) => {
    const clarifications = Array.isArray(task?.clarifications) ? task.clarifications : [];
    const getMs = (value) => {
      if (!value) return 0;
      if (value.toDate) return value.toDate().getTime();
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...clarifications].sort((a, b) => getMs(a.createdAt) - getMs(b.createdAt));
  };

  const getMinutesFromTime = (value) => {
    if (!value || typeof value !== "string" || !value.includes(":")) return null;
    const [hourStr, minuteStr] = value.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    return hour * 60 + minute;
  };

  const sendClarification = async (task, kid) => {
    if (!clarificationsEnabled) return;
    if (!task) return;
    const message = clarifyDrafts[task.id];
    if (!message || !message.trim()) return;
    const actionId = createActionId();
    await runActionWithQueue(
      {
        type: "task.clarify",
        familyId,
        taskId: task.id,
        actionId,
        message,
        authorRole: "kid",
        authorName: kid?.name || "Kid"
      },
      executeAction
    );
    setClarifyDrafts(prev => ({ ...prev, [task.id]: "" }));
    setReplyModal(null);
  };

  const keyboardRows = [
    ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
    ["shift", "z", "x", "c", "v", "b", "n", "m", "back"],
    ["space", ".", "?", "!", "'", "clear"]
  ];

  const getKeyLabel = (key) => {
    if (key === "shift") return keyboardShift ? "⇧" : "⇩";
    if (key === "back") return "⌫";
    if (key === "space") return "Space";
    if (key === "clear") return "Clear";
    return keyboardShift ? key.toUpperCase() : key;
  };

  const handleKeyboardKey = (key) => {
    const taskId = replyModal?.task?.id;
    if (!taskId) return;
    if (key === "shift") {
      setKeyboardShift(prev => !prev);
      return;
    }
    setClarifyDrafts(prev => {
      const current = prev[taskId] || "";
      let next = current;
      if (key === "back") {
        next = current.slice(0, -1);
      } else if (key === "clear") {
        next = "";
      } else if (key === "space") {
        next = `${current} `;
      } else {
        const letter = keyboardShift ? key.toUpperCase() : key;
        next = `${current}${letter}`;
      }
      return { ...prev, [taskId]: next };
    });
  };

  const requestReward = async (kid, reward) => {
    if (!kid || !reward) return;
    const setNotice = (message, type = "error") => {
      setRewardNoticeByKid((prev) => ({
        ...prev,
        [kid.id]: { message, type }
      }));
      window.setTimeout(() => {
        setRewardNoticeByKid((prev) => {
          if (!prev[kid.id] || prev[kid.id].message !== message) return prev;
          const next = { ...prev };
          delete next[kid.id];
          return next;
        });
      }, 2600);
    };

    if ((kid.points || 0) < (reward.cost || 0)) {
      setNotice("Not enough points for that reward.");
      return;
    }
    const alreadyPending = redemptions.some(
      r =>
        r.status === "pending" &&
        r.kidId === kid.id &&
        r.rewardId === reward.id
    );
    if (alreadyPending) {
      setNotice("That reward request is already pending.");
      return;
    }
    const actionId = createActionId();
    await runActionWithQueue(
      {
        type: "reward.request",
        familyId,
        actionId,
        kidId: kid.id,
        kidName: kid.name,
        rewardId: reward.id,
        rewardName: reward.name,
        cost: reward.cost || 0
      },
      executeAction
    );
    setNotice("Reward request sent.", "success");
    showToast({ type: "success", message: `${kid.name} requested ${reward.name}.` });
  };

  const changeDate = (days) => {
    const newDate = new Date(scheduleDate);
    newDate.setDate(newDate.getDate() + days);
    setScheduleDate(newDate);
  };

  const getEffectiveStatus = (task) => {
    return getEffectiveTaskStatus(task, {
      autoFailActive,
      now: zonedNow,
      graceMins,
      localMidnightUtcMs
    });
  };

  const shouldShowTask = (task) => {
    if (!task) return false;
    const status = getEffectiveStatus(task);
    if (status !== "open") return true;
    if (!isRepeatTask(task)) return true;
    const showAfter = task.showAfterTime || "00:01";
    const showAfterMinutes = getMinutesFromTime(showAfter);
    if (showAfterMinutes === null) return true;
    const nowMinutes = zonedNow.getHours() * 60 + zonedNow.getMinutes();
    return nowMinutes >= showAfterMinutes;
  };

  const repeatTasksToReset = useMemo(
    () =>
      tasks.filter(
        (task) =>
          shouldRepeatResetToday(task, zonedNow, localMidnightUtcMs)
      ),
    [tasks, zonedNow, localMidnightUtcMs]
  );

  useEffect(() => {
    if (!familyId) return;
    if (repeatTasksToReset.length === 0) return;
    repeatTasksToReset.forEach((task) => {
      updateDoc(doc(db, "families", familyId, "tasks", task.id), {
        status: "open",
        lastReset: localMidnightUtcMs,
        needsClarification: false,
        approvedAt: null,
        autoApprovedAt: null,
        autoFailedAt: null,
        closedAt: null,
        clarifications: [],
        lastClarificationAt: null
      }).catch((error) => {
        console.warn("Repeat task reset failed", error);
      });
    });
  }, [familyId, repeatTasksToReset, localMidnightUtcMs]);

  const visibleKids = useMemo(() => {
    if (!isKidAccount) return kids;
    if (!Array.isArray(kids) || kids.length === 0) return [];
    let scoped = [];
    if (currentUserId) {
      scoped = kids.filter((kid) => String(kid?.userUid || "").trim() === String(currentUserId).trim());
    }
    if (scoped.length === 0 && normalizedMemberName) {
      scoped = kids.filter((kid) => String(kid?.name || "").trim().toLowerCase() === normalizedMemberName);
    }
    return scoped;
  }, [isKidAccount, kids, currentUserId, normalizedMemberName]);

  return (
    <div className={`board theme-${familySettings?.theme || 'ocean'}`} data-tour="kids-board">
      {replyModal && clarificationsEnabled && (
        <div className="modal-overlay" onClick={() => setReplyModal(null)}>
          <div
            className="glass modal-content kid-reply-modal"
            onClick={(e) => e.stopPropagation()}
            ref={replyModalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Reply to parent"
          >
            <div className="kid-reply-header">
              <div>
                <h3>Reply to Parent</h3>
                <p className="kid-reply-subtitle">
                  {replyModal.kid?.name || "Kid"} • {replyModal.task?.title || "Task"}
                </p>
              </div>
              <div className="kid-reply-actions">
                <button
                  className="btn-nav"
                  onClick={() => {
                    setUseDeviceKeyboard(prev => !prev);
                    setTimeout(() => replyInputRef.current?.focus(), 100);
                  }}
                >
                  {useDeviceKeyboard ? "Hide Keyboard" : "Use Device Keyboard"}
                </button>
                <button className="btn-nav" onClick={() => setReplyModal(null)}>Close</button>
              </div>
            </div>
            <textarea
              ref={replyInputRef}
              className="kid-reply-input"
              placeholder="Type your reply..."
              autoFocus
              readOnly={!useDeviceKeyboard}
              inputMode={useDeviceKeyboard ? "text" : "none"}
              value={clarifyDrafts[replyModal.task?.id] || ""}
              onChange={(e) => {
                const taskId = replyModal?.task?.id;
                if (!taskId) return;
                setClarifyDrafts(prev => ({ ...prev, [taskId]: e.target.value }));
              }}
              rows={4}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {quickReplyOptions.map((option) => (
                <button
                  key={option}
                  className="btn-nav"
                  type="button"
                  onClick={() => {
                    const taskId = replyModal?.task?.id;
                    if (!taskId) return;
                    setClarifyDrafts((prev) => {
                      const current = prev[taskId] || "";
                      const nextValue = current ? `${current} ${option}` : option;
                      return { ...prev, [taskId]: nextValue };
                    });
                    setTimeout(() => replyInputRef.current?.focus(), 20);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
            {!useDeviceKeyboard && (
              <div className="kid-keyboard">
                {keyboardRows.map((row, idx) => (
                  <div key={`row-${idx}`} className="kid-keyboard-row">
                    {row.map((key) => (
                      <button
                        key={key}
                        className={`btn-nav kid-key ${key === "space" ? "kid-key-space" : ""} ${key === "clear" ? "kid-key-clear" : ""}`}
                        onClick={() => handleKeyboardKey(key)}
                      >
                        {getKeyLabel(key)}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div className="kid-reply-actions">
              <button className="btn-nav" onClick={() => setReplyModal(null)}>Cancel</button>
              <button
                className="btn-nav success-btn"
                onClick={() => sendClarification(replyModal.task, replyModal.kid)}
              >
                Send Reply
              </button>
            </div>
          </div>
        </div>
      )}
      {isPro && familySettings?.powerHourEnabled && isWithinPowerHour(
        familySettings?.powerHourStart,
        familySettings?.powerHourEnd
      ) && (
        <div className="power-hour-banner">
          ⚡ Power Hour is active! Bonus points on approvals.
        </div>
      )}
      
      <PinPad 
        isOpen={showPinPad} 
        correctPin={familySettings?.toddlerPin} 
        onConfirm={() => {
          setIsBoardLocked(false);
          setShowPinPad(false);
          if (pendingAction) {
            executeTaskUpdate(pendingAction.task, pendingAction.type);
            setPendingAction(null);
          }
        }} 
        onClose={() => setShowPinPad(false)}
      />

      {familySettings?.toddlerLock && (
        <button className="lock-btn-fixed" onClick={() => isBoardLocked ? setShowPinPad(true) : setIsBoardLocked(true)}>
          {isBoardLocked ? "🔒" : "🔓"}
        </button>
      )}

      <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "10px" }}>
        <button
          className={`btn-nav ${taskView === "today" ? "active-settings-tab" : ""}`}
          onClick={() => setTaskView("today")}
        >
          Today
        </button>
        <button
          className={`btn-nav ${taskView === "all" ? "active-settings-tab" : ""}`}
          onClick={() => setTaskView("all")}
        >
          All
        </button>
      </div>


      {/* 🎁 UP FOR GRABS COLUMN */}
      <div className="glass column">
        <div className="col-header">🎁 Up For Grabs</div>
        <div className="card-body-scroll touch-scroll">
          {tasks.filter(t => t.assignedTo === "unassigned" && getEffectiveStatus(t) === "open" && shouldShowTask(t) && matchesView(t)).map(t => (
            <div
              key={t.id}
              className={`card ${isPro && isTaskDueSoon(t, 120, zonedNow) ? "task-due-soon" : ""} ${isPro && isTaskOverdue(t, zonedNow, graceMins) ? "task-overdue" : ""}`}
            >
              <div className="card-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>{t.title}</h3>
                <span className="pts-tag">⭐ {t.points}</span>
              </div>
              {getTaskTimeLabel(t) && (
                <div className="task-time">{getTaskTimeLabel(t)}</div>
              )}
              {isPro && isTaskDueSoon(t, 120, zonedNow) && (
                <div className="task-due-badge">Due soon</div>
              )}
              {isPro && isTaskOverdue(t, zonedNow, graceMins) && (
                <div className="task-overdue-badge">Overdue</div>
              )}
              <div className="kid-claim-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '12px' }}>
                {visibleKids.map(k => (
                  <button 
                    key={k.id} 
                    className={`btn-nav ${k.isFrozen ? 'frozen-btn-locked' : ''}`}
                    disabled={k.isFrozen}
                    onClick={() => handleInteraction(t, k.id)}
                  >
                    {k.isFrozen ? "❄️" : k.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
              {tasks.filter(t => t.assignedTo === "unassigned" && getEffectiveStatus(t) === "open" && shouldShowTask(t) && matchesView(t)).length === 0 && (
                <p style={{ textAlign: "center", color: "white", opacity: 0.6, marginTop: "20px" }}>
                  No chores available right now.
                </p>
              )}
              {isKidAccount && visibleKids.length === 0 && (
                <p style={{ textAlign: "center", color: "white", opacity: 0.7, marginTop: "12px" }}>
                  Your kid login is not linked to a kid profile yet. Ask a parent to reconnect your kid account.
                </p>
              )}
            </div>
          </div>

          {/* 🧒 KID COLUMNS */}
      {visibleKids.map(kid => {
        const goal = kid.rewardGoal || 100;
        const progress = Math.min((kid.points / goal) * 100, 100);

        return (
          <div className={`glass column kid-column ${kid.isFrozen ? "column-frozen" : ""}`} key={kid.id}>
            <div className="col-header">
              <div className="kid-name-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{kid.isFrozen ? "❄️ " : ""}{kid.name}</span>
                <span>💰 {kid.points || 0}</span>
              </div>
              {isPro && (
                <div className="kid-meta">
                  {kid.streakCount ? <span>🔥 {kid.streakCount} day streak</span> : <span>—</span>}
                  {Array.isArray(kid.badges) && kid.badges.length > 0 && (
                    <span className="kid-badges">{kid.badges.join(", ")}</span>
                  )}
                </div>
              )}
              {kid.showRewardTracker && (
                <div className="reward-track-container">
                    <div className="reward-bar" style={{ width: `${progress}%` }}></div>
                    <span className="reward-text">{kid.rewardName || "Reward"}:  {kid.points}/{goal}</span>
                </div>
              )}
            </div>

            <div className="card-body-scroll touch-scroll">
              {tasks.filter(t => t.assignedTo === kid.id && (getEffectiveStatus(t) === 'open' || getEffectiveStatus(t) === 'failed' || getEffectiveStatus(t) === 'pending') && shouldShowTask(t) && matchesView(t)).map(t => {
                const status = getEffectiveStatus(t);
                const clarifications = clarificationsEnabled ? getClarifications(t) : [];
                const needsClarify = clarificationsEnabled && t.needsClarification;
                return (
                <div
                  key={t.id}
                  className={`card ${(status === 'failed' || needsClarify) ? 'card-failed' : ''} ${status === 'pending' ? 'card-pending' : ''} ${status === 'open' && isPro && isTaskDueSoon(t, 120, zonedNow) ? 'task-due-soon' : ''} ${status === 'open' && isPro && isTaskOverdue(t, zonedNow, graceMins) ? 'task-overdue' : ''}`}
                >
                  <div className="card-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0 }}>{t.title}</h3>
                    <span className="pts-tag">⭐ {t.points}</span>
                  </div>
                  {getTaskTimeLabel(t) && (
                    <div className="task-time">{getTaskTimeLabel(t)}</div>
                  )}
                  {isPro && status === "open" && isTaskDueSoon(t, 120, zonedNow) && (
                    <div className="task-due-badge">Due soon</div>
                  )}
                  {isPro && status === "open" && isTaskOverdue(t, zonedNow, graceMins) && (
                    <div className="task-overdue-badge">Overdue</div>
                  )}
                  
                  {needsClarify && <p className="warning-text-red">⚠️ CHECK WITH PARENT</p>}
                  {status === 'failed' && <p className="status-label error">❌ TRY AGAIN! </p>}
                  
                  {/* PENDING STATUS MESSAGE */}
                  {status === 'pending' && (
                    <p className="status-label" style={{ textAlign: 'center', marginTop: '10px' }}>⏳ Pending Approval...</p>
                  )}

                  {clarifications.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '0.85rem', opacity: 0.9 }}>
                      {clarifications.map((note, idx) => (
                        <div key={note.id || `${t.id}-note-${idx}`} style={{ marginBottom: '4px' }}>
                          <strong>{note.authorRole === "kid" ? (note.authorName || "Kid") : "Parent"}:</strong>{" "}
                          {note.message}
                        </div>
                      ))}
                    </div>
                  )}
                  {(needsClarify || clarifications.length > 0) && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        className="btn-nav"
                        onClick={() => {
                          if (!clarificationsEnabled) return;
                          setReplyModal({ task: t, kid });
                        }}
                      >
                        Reply
                      </button>
                    </div>
                  )}
                  
                  {status !== 'pending' && (
                    <button className="btn-nav success-btn" style={{ width: '100%', marginTop: '10px' }} onClick={() => handleInteraction(t, "pending")}>
                      Done
                    </button>
                  )}
                </div>
                );
              })}
              {tasks.filter(t => t.assignedTo === kid.id && (getEffectiveStatus(t) === 'open' || getEffectiveStatus(t) === 'failed' || getEffectiveStatus(t) === 'pending') && shouldShowTask(t) && matchesView(t)).length === 0 && (
                <p style={{ textAlign: 'center', color: 'white', opacity: 0.6, marginTop: '20px' }}>
                  No chores right now.
                </p>
              )}

              {isPro && rewards.filter(r => r.active !== false).length > 0 && (
                <div className="reward-shop">
                  <div className="reward-shop-title">Rewards</div>
                  {rewardNoticeByKid[kid.id]?.message && (
                    <small
                      className={rewardNoticeByKid[kid.id]?.type === "success" ? "success-message" : "pin-error"}
                      role={rewardNoticeByKid[kid.id]?.type === "success" ? "status" : "alert"}
                      style={{ display: "block", marginBottom: 8, textAlign: "left" }}
                    >
                      {rewardNoticeByKid[kid.id]?.message}
                    </small>
                  )}
                  {rewards.filter(r => r.active !== false).map(r => {
                    const pending = redemptions.some(
                      item => item.status === "pending" && item.kidId === kid.id && item.rewardId === r.id
                    );
                    return (
                      <div key={r.id} className="reward-item">
                        <div className="reward-item-info">
                          <strong>{r.name}</strong>
                          <span className="reward-cost">⭐ {r.cost || 0}</span>
                        </div>
                        <button
                          className="btn-nav"
                          disabled={pending}
                          onClick={() => requestReward(kid, r)}
                        >
                          {pending ? "Pending" : "Request"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* 📅 SCHEDULE COLUMN */}
      <div className="glass column">
        <div className="col-header">📅 Schedule</div>
        <div className="date-nav" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <button className="btn-nav" style={{ padding: '5px 15px' }} onClick={() => changeDate(-1)}>←</button>
            <span style={{ color: 'white', fontWeight: 'bold' }}>{scheduleDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
            <button className="btn-nav" style={{ padding: '5px 15px' }} onClick={() => changeDate(1)}>→</button>
        </div>
        <div className="card-body-scroll touch-scroll">
          {[...calendarEvents]
            .filter(e => isSameDay(e.date, scheduleDate))
            .sort((a, b) => String(a.time || "23:59").localeCompare(String(b.time || "23:59")))
            .map((event, idx) => (
            <div key={idx} className="card" style={{ color: 'var(--text-dark)' }}>
              <h4 style={{ margin: 0 }}>{event.title}</h4>
              <small style={{ color: 'var(--accent)', fontWeight: 'bold' }}>{event.time}</small>
            </div>
          ))}
          {calendarEvents.filter(e => isSameDay(e.date, scheduleDate)).length === 0 && (
            <p style={{ textAlign: 'center', color: 'white', opacity: 0.6, marginTop: '20px' }}>Nothing scheduled.</p>
          )}
        </div>
      </div>
    </div>
  );
}
